import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import DownloadModel from "../../../assets/TailoredGeneration/icons/api-download-model.svg";
import GenerateImages from "../../../assets/TailoredGeneration/icons/api-generate-images.svg";
import ManageDatasets from "../../../assets/TailoredGeneration/icons/api-manage-datasets.svg";
import ManageProjects from "../../../assets/TailoredGeneration/icons/api-manage-projects.svg";
import RestylePortraits from "../../../assets/TailoredGeneration/icons/api-restyle-portraits.svg";
import TrainModels from "../../../assets/TailoredGeneration/icons/api-train-models.svg";
import UploadModel from "../../../assets/TailoredGeneration/icons/api-upload-model.svg";
import TgGenerateVideosIcon from "../../../assets/TailoredGeneration/icons/tg-generate-videos.svg";
import { ConsoleAPICardProps } from "../../../components/Home/API/Cards/Card/ConsoleAPICard";
import ConsoleAPICards from "../../../components/Home/API/Cards/ConsoleAPICards";
import ConsoleAPILayout from "../../../components/Home/API/ConsoleAPILayout";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import LinkConstants from "../../../constants/LinkConstants";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { usePostHog } from "../../../hooks/usePostHog";

const TgAPI = () => {
	const { t } = useTranslation("tg", { keyPrefix: "api" });
	const { trackEvent } = usePostHog();

	const openDocs = (link: string) => {
		// Track API documentation viewed
		trackEvent(POSTHOG_EVENTS.API_DOCUMENTATION_VIEWED, {
			section: getSectionFromLink(link),
		});

		window.open(link, "_blank");
	};

	// Helper to extract section name from link
	const getSectionFromLink = (link: string): string => {
		const sectionMap: { [key: string]: string } = {
			[LinkConstants.TG.CREATE_PROJECT.DOCS]: "Manage Projects",
			[LinkConstants.TG.CREATE_DATASET.DOCS]: "Manage Datasets",
			[LinkConstants.TG.TRAIN_MODEL.DOCS]: "Train Models",
			[LinkConstants.TG.DOWNLOAD_MODEL.DOCS]: "Download Model",
			[LinkConstants.TG.UPLOAD_MODEL.DOCS]: "Upload Model",
			[LinkConstants.TG.GENERATE_IMAGES.DOCS]: "Generate Images",
			[LinkConstants.TG.RESTYLE_PORTRAITS.DOCS]: "Restyle Portraits",
			[LinkConstants.TG.VIDEO_GENERATION.DOCS]: "Generate Videos",
		};

		return sectionMap[link] || "Tailored Generation";
	};

	const tgApiCards: ConsoleAPICardProps[] = [
		{
			image: ManageProjects,
			imageType: "icon",
			header: t("cards.manageProjects.header"),
			description: t("cards.manageProjects.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.CREATE_PROJECT.DOCS)}>
					{t("cards.manageProjects.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.CREATE_PROJECT.DOCS),
		},
		{
			image: ManageDatasets,
			imageType: "icon",
			header: t("cards.manageDatasets.header"),
			description: t("cards.manageDatasets.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.CREATE_DATASET.DOCS)}>
					{t("cards.manageDatasets.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.CREATE_DATASET.DOCS),
		},
		{
			image: TrainModels,
			imageType: "icon",
			header: t("cards.trainModels.header"),
			description: t("cards.trainModels.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.TRAIN_MODEL.DOCS)}>
					{t("cards.trainModels.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.TRAIN_MODEL.DOCS),
		},
		{
			image: DownloadModel,
			imageType: "icon",
			header: t("cards.downloadModel.header"),
			description: t("cards.downloadModel.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.DOWNLOAD_MODEL.DOCS)}>
					{t("cards.downloadModel.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.DOWNLOAD_MODEL.DOCS),
		},
		{
			image: UploadModel,
			imageType: "icon",
			header: t("cards.uploadModel.header"),
			description: t("cards.uploadModel.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.UPLOAD_MODEL.DOCS)}>
					{t("cards.uploadModel.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.UPLOAD_MODEL.DOCS),
		},
		{
			image: GenerateImages,
			imageType: "icon",
			header: t("cards.generateImages.header"),
			description: t("cards.generateImages.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.GENERATE_IMAGES.DOCS)}>
					{t("cards.generateImages.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.GENERATE_IMAGES.DOCS),
		},
		{
			image: TgGenerateVideosIcon,
			imageType: "icon",
			header: t("cards.generateVideos.header"),
			description: t("cards.generateVideos.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.VIDEO_GENERATION.DOCS)}>
					{t("cards.generateVideos.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.VIDEO_GENERATION.DOCS),
		},
		{
			image: RestylePortraits,
			imageType: "icon",
			header: t("cards.restylePortraits.header"),
			description: t("cards.restylePortraits.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.TG.RESTYLE_PORTRAITS.DOCS)}>
					{t("cards.restylePortraits.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.TG.RESTYLE_PORTRAITS.DOCS),
		},
	];

	return (
		<ConsoleAPILayout description={t("description")}>
			<ConsoleAPICards cards={tgApiCards} />
		</ConsoleAPILayout>
	);
};

export default observer(TgAPI);
